import { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { ShieldCheck } from "lucide-react";
import { supabase } from "@/integrations/supabase/client";
import { SEOHead } from "@/components/SEOHead";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";

type OAuthError = { message: string };
type OAuthDetails = {
  client?: { name?: string; client_name?: string };
  redirect_url?: string;
  redirect_to?: string;
};
type OAuthDecision = { redirect_url?: string; redirect_to?: string };
type OAuthNamespace = {
  getAuthorizationDetails: (authorizationId: string) => Promise<{ data: OAuthDetails | null; error: OAuthError | null }>;
  approveAuthorization: (authorizationId: string) => Promise<{ data: OAuthDecision | null; error: OAuthError | null }>;
  denyAuthorization: (authorizationId: string) => Promise<{ data: OAuthDecision | null; error: OAuthError | null }>;
};

function oauthClient(): OAuthNamespace | undefined {
  return (supabase.auth as unknown as { oauth?: OAuthNamespace }).oauth;
}

function currentRelativeUrl() {
  return `${window.location.pathname}${window.location.search}${window.location.hash}`;
}

export default function OAuthConsent() {
  const [params] = useSearchParams();
  const authorizationId = params.get("authorization_id") ?? "";
  const [details, setDetails] = useState<OAuthDetails | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    let active = true;

    const loadDetails = async () => {
      if (!authorizationId) {
        setError("Missing authorization request.");
        return;
      }

      const oauth = oauthClient();
      if (!oauth) {
        setError("OAuth authorization is not available for this Supabase project yet.");
        return;
      }

      const { data: sessionData } = await supabase.auth.getSession();
      if (!sessionData.session) {
        window.location.href = `/auth?next=${encodeURIComponent(currentRelativeUrl())}`;
        return;
      }

      const { data, error: detailsError } = await oauth.getAuthorizationDetails(authorizationId);
      if (!active) return;
      if (detailsError) {
        setError(detailsError.message);
        return;
      }

      const immediateRedirect = data?.redirect_url ?? data?.redirect_to;
      if (immediateRedirect && !data?.client) {
        window.location.href = immediateRedirect;
        return;
      }

      setDetails(data ?? {});
    };

    loadDetails();
    return () => {
      active = false;
    };
  }, [authorizationId]);

  const decide = async (approve: boolean) => {
    const oauth = oauthClient();
    if (!oauth) {
      setError("OAuth authorization is not available for this Supabase project yet.");
      return;
    }

    setBusy(true);
    const { data, error: decisionError } = approve
      ? await oauth.approveAuthorization(authorizationId)
      : await oauth.denyAuthorization(authorizationId);

    if (decisionError) {
      setBusy(false);
      setError(decisionError.message);
      return;
    }

    const redirectTarget = data?.redirect_url ?? data?.redirect_to;
    if (!redirectTarget) {
      setBusy(false);
      setError("No redirect was returned by the authorization server.");
      return;
    }

    window.location.href = redirectTarget;
  };

  const clientName = details?.client?.name ?? details?.client?.client_name ?? "this agent client";

  return (
    <>
      <SEOHead title="Connect Agent Client - Andean Bloom" description="Approve an OAuth agent integration for Andean Bloom." />
      <div className="min-h-screen flex items-center justify-center px-4 py-12 bg-background">
        <Card className="w-full max-w-lg">
          <CardHeader className="text-center">
            <div className="mx-auto mb-4 flex h-12 w-12 items-center justify-center rounded-full bg-primary/10 text-primary">
              <ShieldCheck className="h-6 w-6" />
            </div>
            <CardTitle>Connect {clientName}</CardTitle>
            <CardDescription>Approve this agent integration to use Andean Bloom as your signed-in account.</CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            {error ? (
              <p className="text-sm text-destructive">Could not load this authorization request: {error}</p>
            ) : details ? (
              <div className="grid gap-3 sm:grid-cols-2">
                <Button onClick={() => decide(true)} disabled={busy}>
                  {busy ? "Working..." : "Approve"}
                </Button>
                <Button variant="outline" onClick={() => decide(false)} disabled={busy}>
                  Deny
                </Button>
              </div>
            ) : (
              <p className="text-sm text-muted-foreground">Loading authorization request...</p>
            )}
          </CardContent>
        </Card>
      </div>
    </>
  );
}
